import React, { useRef, useEffect } from "react";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import { Button } from "components/common/Button";
import { SelectCheckbox } from "components/common/SelectCheckbox";

interface FilterCategories {
  all: boolean;
  requests: boolean;
  updates: boolean;
}
interface FilterDropdownProps {
  filterCategories: FilterCategories;
  showFilterDropdown: boolean;
  onToggleDropdown: () => void;
  onCategoryFilterChange: (category: "all" | "requests" | "updates") => void;
  onClose: () => void;
  size?: "sm" | "md";
}

export const FilterDropdown: React.FC<FilterDropdownProps> = ({
  filterCategories,
  showFilterDropdown,
  onToggleDropdown,
  onCategoryFilterChange,
  onClose,
  size = "md",
}) => {
  const filterDropdownRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (wrapperRef.current && !wrapperRef.current.contains(target)) {
        onClose();
      }
    };

    if (showFilterDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showFilterDropdown, onClose]);

  const filtersApplied = filterCategories.requests || filterCategories.updates;
  return (
    <div ref={wrapperRef} className="relative">
      <Button
        variant="default"
        onClick={(e) => {
          e.stopPropagation();
          if (showFilterDropdown) {
            onClose();
          } else {
            onToggleDropdown();
          }
        }}
        size={size}
        className={filtersApplied ? "button-active-state" : ""}
      >
        <FilterAltOutlinedIcon
          sx={{
            fontSize: size === "sm" ? 20 : undefined,
            color: filtersApplied ? "var(--color-accent)" : undefined,
          }}
        />
      </Button>

      {showFilterDropdown && (
        <div
          ref={filterDropdownRef}
          className="absolute right-0 top-8 bg-bg-subtle border rounded-app py-2 px-3 w-32 z-60"
        >
          <div className="space-y-2">
            <button
              className={`w-full text-left outline-none transition-colors ${
                filterCategories.requests && filterCategories.updates
                  ? "text-fg-semibold"
                  : "text-fg-regular"
              }`}
              onClick={() => onCategoryFilterChange("all")}
            >
              All
            </button>
            <div className="flex items-center gap-2 cursor-pointer">
              <SelectCheckbox
                selected={filterCategories.requests}
                onToggle={() => onCategoryFilterChange("requests")}
                size="sm"
              />
              <span className="text-fg-muted-regular">Requests</span>
            </div>
            <div className="flex items-center gap-2 cursor-pointer">
              <SelectCheckbox
                selected={filterCategories.updates}
                onToggle={() => onCategoryFilterChange("updates")}
                size="sm"
              />
              <span className="text-fg-muted-regular">Updates</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
